// ============================================================================
// PsyResultsTable — ЕДИНЫЙ вид протоколов пациента (SSoT рендера).
//
// Используется кабинетом пациента. БЕЗ таблиц: карточки одинаково читаемы на
// телефоне и на десктопе (дата, методика, балл, порог, заключение).
// Пустой список — явный текст (разный контекст задаётся emptyText).
// ============================================================================

import { FlaskConical } from 'lucide-react';
import { formatValue } from '@/lib/format';
import type { PatientResult } from '@/lib/psyWorkspace';

/** Короткая сводка результата: балл и признак порога. */
export function resultSummary(r: PatientResult): string {
  const parts: string[] = [];
  if (r.score) parts.push(`балл: ${r.score}`);
  if (r.passed) parts.push('порог пройден');
  return parts.join(' · ');
}

export function PsyResultsTable({ results, emptyText }: {
  results: PatientResult[];
  emptyText?: string;
}) {
  if (results.length === 0) {
    return <div className="text-xs text-muted-foreground">{emptyText || 'Результатов пока нет.'}</div>;
  }
  return (
    <div className="space-y-1.5">
      {results.map((r) => (
        <article key={r.id} className="rounded-lg border bg-card px-3 py-2">
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <FlaskConical className="h-3 w-3 shrink-0 text-primary" />
                <span className="truncate text-sm font-medium">{r.methodName || r.methodCode || 'Методика'}</span>
              </div>
              <div className="text-[11px] text-muted-foreground">
                {r.createdAt ? formatValue(r.createdAt, '', 'timestamp') : 'дата не указана'}
              </div>
            </div>
            <div className="flex shrink-0 flex-wrap justify-end gap-1">
              {r.score && (
                <span className="rounded-full border bg-muted/40 px-1.5 py-0.5 text-[10px]">балл: {r.score}</span>
              )}
              <span className={`rounded-full border px-1.5 py-0.5 text-[10px] ${
                r.passed
                  ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                  : 'border-border bg-muted/50 text-muted-foreground'
              }`}>
                {r.passed ? 'порог пройден' : 'порог не пройден'}
              </span>
            </div>
          </div>
          {r.conclusion && (
            <details className="mt-1.5 text-xs">
              <summary className="cursor-pointer select-none text-muted-foreground">
                <span className="line-clamp-1 inline align-middle">{r.conclusion}</span>
              </summary>
              <div className="mt-1 whitespace-pre-wrap break-words text-foreground">{r.conclusion}</div>
            </details>
          )}
        </article>
      ))}
    </div>
  );
}
